import React, { useEffect, useMemo, useState } from 'react'
import Tesseract from 'tesseract.js'
import {
  CBadge,
  CButton,
  CCard,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheckCircle,
  cilImage,
  cilPeople,
  cilUserPlus,
  cilWarning,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  ImportFileBar,
  ImportUploadStep,
  InfoBox,
  MiniStepper,
  StatTile,
  tintStyle,
} from 'src/components/common/ImportWizard'
import { MATCH_META, buildPlayerIndex, resolvePlayer } from 'src/components/common/playerMatching'
import { POSITION_META } from '../ScoreCard/scorecardConfig'

const WIZARD_STEPS = ['Upload Image', 'Validate Players', 'Review & Import']

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  unmatched_player: {
    label: 'Player Not Found',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  ambiguous_player: {
    label: 'Several Players Match',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  duplicate: { label: 'Read Twice', color: 'warning', icon: cilWarning, accent: '#b45309' },
  nothing_to_do: {
    label: 'Already In Squad',
    color: 'info',
    icon: cilCheckCircle,
    accent: '#087990',
  },
}

const playerLabel = (player) => {
  const position = POSITION_META[player?.position]
  const suffix = [position?.label, player?.team_name].filter(Boolean).join(' · ')
  return suffix ? `${player.full_name} (${suffix})` : player?.full_name || ''
}

const normalizeText = (text) =>
  String(text || '')
    // Newlines are kept: on a team sheet they are what separates one player from the next.
    .replace(/\r\n|\r/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/[’‘`]/g, "'")
    .trim()

// A leading "1." or "12)" is the sheet's own numbering rather than part of the name, and OCR
// reads specks on a photo as stray punctuation. No player's name starts with a digit, so
// anything before the first letter can go.
const cleanName = (raw) =>
  String(raw)
    .replace(/^[^A-Za-z]+/, '')
    .replace(/\s+/g, ' ')
    .trim()

// A line with barely a letter on it is a page number, a column rule or a smudge, not a player.
const looksLikeName = (value) => (value.match(/[A-Za-z]/g) || []).length >= 2

// OCR hands back one blob of text and the sheet decides how to cut it up: a sheet written as one
// comma-separated line splits on the commas - where a line break mid-name is just a space - and
// anything else is one name per line.
export const readNames = (rawText) => {
  const text = normalizeText(rawText)
  if (!text) return []
  const parts = text.includes(',') ? text.replace(/\n+/g, ' ').split(',') : text.split('\n')
  return parts.map(cleanName).filter(looksLikeName)
}

// Re-run over every name after each mapping change: which player one row points at decides
// whether another row is now the same player read twice, so a row cannot be re-checked alone.
const buildValidatedRows = (baseRows, index, squadIds) => {
  const claimed = new Set()

  return baseRows.map((base, i) => {
    const { record: player, match, ambiguous } = resolvePlayer(base, index)
    const inSquad = Boolean(player && squadIds.has(String(player.id)))

    // A team sheet carries names and nothing else, so a player already picked has nothing left
    // to import - unlike the spreadsheet import, where their figures may still be on the row.
    let result = 'valid'
    if (!player) {
      result = ambiguous ? 'ambiguous_player' : 'unmatched_player'
    } else if (claimed.has(String(player.id))) {
      result = 'duplicate'
    } else if (inSquad) {
      result = 'nothing_to_do'
    }

    if (result === 'valid') claimed.add(String(player.id))

    return {
      ...base,
      row: i + 1,
      player_key: player ? String(player.id) : '',
      player_name: player?.full_name || '',
      player_position: player?.position,
      team_name: player?.team_name || '',
      match,
      ambiguous_count: ambiguous?.length || 0,
      in_squad: inSquad,
      result,
      included: result === 'valid',
    }
  })
}

// Squad Import from a team sheet photo: the names are read off the image in the browser and
// matched against the club's player pool, by name where the two agree and by hand where they do
// not. Same 3-step shape as the spreadsheet imports; see the README.
const ImportPlayerSquad = ({ players, squad, onImport, onCancel }) => {
  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)

  const [rows, setRows] = useState([])

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  const playerPool = useMemo(() => players || [], [players])

  const squadIds = useMemo(
    () => new Set((squad || []).map((picked) => String(picked.player_id))),
    [squad],
  )

  const playerIndex = useMemo(
    () =>
      buildPlayerIndex(playerPool, {
        getKey: (player) => player.id,
        getPlayerId: (player) => player.id,
        getName: (player) => player.full_name,
      }),
    [playerPool],
  )

  const playerOptions = useMemo(
    () =>
      playerPool
        .map((player) => ({ value: String(player.id), label: playerLabel(player) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [playerPool],
  )

  // The preview is an object URL over the picked file. Released by this effect's own cleanup, so
  // replacing the image, resetting the wizard and leaving the page all free it.
  useEffect(() => {
    if (!imageUrl) return undefined
    return () => URL.revokeObjectURL(imageUrl)
  }, [imageUrl])

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setImageUrl('')
    setProgress(0)
    setRows([])
    setImportErrors([])
  }

  const handleFile = async (file) => {
    if (!file) return
    setBusy(true)
    setProgress(0)
    setImportErrors([])

    let names = []
    try {
      const result = await Tesseract.recognize(file, 'eng', {
        logger: (m) =>
          m.status === 'recognizing text' && setProgress(Math.round((m.progress || 0) * 100)),
      })
      names = readNames(result?.data?.text)
    } catch (e) {
      ToastComponent('Could not read this image. Please try a clearer photo.', 'error')
      setBusy(false)
      return
    }

    if (!names.length) {
      ToastComponent('No names could be read from this image.', 'error')
      setBusy(false)
      return
    }

    setImageUrl(URL.createObjectURL(file))
    setFileName(file.name)
    setRows(
      buildValidatedRows(
        names.map((name) => ({ csv_name: name, csv_player_id: '', manual_id: '' })),
        playerIndex,
        squadIds,
      ),
    )
    setBusy(false)
  }

  const remapRow = (row, playerKey) => {
    setRows((prev) => {
      const baseRows = prev.map((r) => (r.row === row ? { ...r, manual_id: playerKey } : r))
      const revalidated = buildValidatedRows(baseRows, playerIndex, squadIds)
      // A row just pointed somewhere comes back included; every other row keeps the
      // include/exclude decision already made for it.
      return revalidated.map((r, idx) => ({
        ...r,
        included: r.result === 'valid' && (r.row === row || prev[idx].included),
      }))
    })
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    const alreadyIn = rows.filter((r) => r.result === 'nothing_to_do').length
    return { valid, alreadyIn, needsAttention: rows.length - valid - alreadyIn }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)
  const newPlayerIds = [...new Set(readyRows.map((r) => r.player_key))]

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const res = await onImport(newPlayerIds)
      if (res?.success) {
        resetAll()
      } else {
        setImportErrors(
          Array.isArray(res?.errors) && res.errors.length
            ? res.errors
            : ['Something went wrong. Please try again.'],
        )
      }
    } catch (e) {
      setImportErrors(['Something went wrong. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  const renderUpload = () => (
    <>
      <ImportUploadStep
        templateTitle="Before You Upload"
        templateHint="The names are read off the image in your browser and matched against this club's players. Nothing is added until you have reviewed them."
        requirementsTitle="What reads best"
        requiredColumns={[
          <>
            One name per line{' '}
            <span className="text-medium-emphasis">(or names separated by commas)</span>
          </>,
          <>
            A straight-on photo or screenshot{' '}
            <span className="text-medium-emphasis">(not taken at an angle)</span>
          </>,
          <>
            Printed or clearly written names{' '}
            <span className="text-medium-emphasis">(handwriting is hit and miss)</span>
          </>,
        ]}
        info={
          <>
            Numbering such as &ldquo;1.&rdquo; in front of a name is ignored. Any name that cannot
            be matched gets a dropdown on the next step for you to point at the right player, and
            players already in the squad are skipped.
          </>
        }
        uploadTitle="Upload Team Sheet"
        uploadHint="Drag and drop a photo or screenshot, or browse your computer."
        accept="image/*"
        dropPrompt={
          <>
            Drag and drop a team sheet image, or <span className="text-primary">browse</span>
          </>
        }
        fileName={fileName}
        filePreview={imageUrl}
        fileDetail={`${rows.length} name${rows.length === 1 ? '' : 's'} read`}
        busy={busy}
        busyLabel={
          progress > 0 ? `Reading the team sheet… ${progress}%` : 'Reading the team sheet…'
        }
        onFile={handleFile}
        onRemove={resetAll}
      />

      <div className="d-flex justify-content-between mt-4">
        {onCancel ? (
          <CButton type="button" color="secondary" variant="outline" onClick={onCancel}>
            Cancel
          </CButton>
        ) : (
          <span />
        )}
        <CButton type="button" color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review Names
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <ImportFileBar
        fileName={fileName}
        detail={`${rows.length} name${rows.length === 1 ? '' : 's'} read from this image`}
        onReplace={resetAll}
      />

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Already In Squad" value={summary.alreadyIn} tone="info" icon={cilPeople} />
      </div>

      <InfoBox color="light">
        A photograph is not a reliable spelling, so every name gets a dropdown &mdash; not only the
        ones we could not place. Check the matches before you continue.
      </InfoBox>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>Name On Sheet</CTableHeaderCell>
                <CTableHeaderCell style={{ minWidth: 280 }}>Player</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const matchMeta = MATCH_META[r.match]
                return (
                  <CTableRow
                    key={r.row}
                    style={r.result === 'valid' ? undefined : tintStyle(meta.accent, '0d')}
                  >
                    <CTableDataCell className="ps-4 text-medium-emphasis">{r.row}</CTableDataCell>

                    <CTableDataCell>
                      <div className="fw-semibold">{r.csv_name || '—'}</div>
                    </CTableDataCell>

                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={r.player_key}
                        onChange={(e) => remapRow(r.row, e.target.value)}
                      >
                        {/* Clearing a row that was pointed somewhere by hand puts it back on
                            whatever the name matched by itself, rather than emptying it. */}
                        <option value="">
                          {r.manual_id ? 'Undo manual mapping' : 'Select player'}
                        </option>
                        {playerOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </CFormSelect>
                      {matchMeta && (
                        <CBadge
                          color={matchMeta.color}
                          shape="rounded-pill"
                          className="px-2 py-1 mt-1"
                        >
                          {matchMeta.label}
                        </CBadge>
                      )}
                      {r.result === 'ambiguous_player' && (
                        <div className="small text-medium-emphasis mt-1">
                          {r.ambiguous_count} players share this name — pick the right one.
                        </div>
                      )}
                      {r.result === 'unmatched_player' && (
                        <div className="small text-medium-emphasis mt-1">
                          No player of this name — pick them from the list, or leave the row out.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1">
                        {meta.label}
                      </CBadge>
                      {r.result === 'duplicate' && (
                        <div className="small text-medium-emphasis mt-1">
                          {r.player_name} is already on an earlier row.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {r.result === 'valid' && (
                        <CButton
                          type="button"
                          size="sm"
                          color={r.included ? 'danger' : 'success'}
                          variant="outline"
                          title={r.included ? 'Exclude from import' : 'Include in import'}
                          onClick={() => toggleRowIncluded(r.row)}
                        >
                          {r.included ? 'Exclude' : 'Include'}
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {summary.needsAttention > 0 && (
        <InfoBox color="light">
          Rows that need attention are left out of the import. Point one at the right player to fix
          it, or leave it — the rest will still import.
        </InfoBox>
      )}

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CButton
          type="button"
          color="primary"
          disabled={!readyRows.length}
          onClick={() => goToStep(3)}
        >
          Continue
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Names Read" value={rows.length} tone="secondary" icon={cilImage} />
        <StatTile
          label="Ready to Add"
          value={newPlayerIds.length}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Excluded"
          value={rows.length - readyRows.length}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Squad After Import"
          value={squadIds.size + newPlayerIds.length}
          tone="info"
          icon={cilPeople}
        />
      </div>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Player</CTableHeaderCell>
                <CTableHeaderCell>Position</CTableHeaderCell>
                <CTableHeaderCell>Team</CTableHeaderCell>
                <CTableHeaderCell>Name On Sheet</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {readyRows.map((r) => (
                <CTableRow key={r.row}>
                  <CTableDataCell className="ps-4 fw-semibold">{r.player_name}</CTableDataCell>
                  <CTableDataCell>{POSITION_META[r.player_position]?.label || '—'}</CTableDataCell>
                  <CTableDataCell>{r.team_name || '—'}</CTableDataCell>
                  <CTableDataCell className="text-medium-emphasis">
                    {r.csv_name || '—'}
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(2)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CLoadingButton
          type="button"
          color="success"
          loading={importing}
          disabled={!newPlayerIds.length}
          onClick={runImport}
        >
          <CIcon icon={cilUserPlus} className="me-1" />
          Add {newPlayerIds.length} Player{newPlayerIds.length === 1 ? '' : 's'}
        </CLoadingButton>
      </div>
    </>
  )

  if (!playerPool.length) {
    return (
      <InfoBox color="light">
        There are no players to pick from yet. Add players to the club before importing a squad.
      </InfoBox>
    )
  }

  return (
    <>
      <MiniStepper
        steps={WIZARD_STEPS}
        current={wizardStep}
        maxReached={maxWizardStep}
        onStepClick={goToStep}
      />
      <ImportErrorAlert errors={importErrors} />
      {wizardStep === 1 && renderUpload()}
      {wizardStep === 2 && renderValidate()}
      {wizardStep === 3 && renderReview()}
    </>
  )
}

export default ImportPlayerSquad
